import { useEffect, type ReactNode } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { Button } from './button'
import { TextField } from './text-field'
import { cn } from '@/shared/lib/cn'

export interface SearchFormProps {
  searchText: string
  onSearchChange: (searchText: string) => void
  label: ReactNode
  placeholder?: string
  className?: string
  onClear?: () => void
}

export function SearchForm({ searchText, onSearchChange, label, placeholder, className, onClear }: SearchFormProps) {
  const { t } = useTranslation()
  const { control, reset } = useForm({ defaultValues: { searchText } })
  const watchedSearchText = useWatch({ control, name: 'searchText' }) ?? ''

  useEffect(() => {
    reset({ searchText })
  }, [searchText, reset])

  useEffect(() => {
    const value = watchedSearchText.trim()
    if (value === searchText) return
    const timeoutId = window.setTimeout(() => onSearchChange(value), 2_000)
    return () => window.clearTimeout(timeoutId)
  }, [watchedSearchText, searchText, onSearchChange])

  function handleClear() {
    reset({ searchText: '' })
    if (searchText !== '') onSearchChange('')
    onClear?.()
  }

  return <div className={cn('flex items-end gap-3', className)}>
    <div className="min-w-0 flex-1">
      <Controller name="searchText" control={control} render={({ field }) =>
        <TextField label={label} value={field.value} onChange={field.onChange} onBlur={field.onBlur}
          ref={field.ref} placeholder={placeholder}
          leadingContent={<Search aria-hidden="true" className="h-4 w-4" />} />
      } />
    </div>
    <Button type="button" variant="secondary" onPress={handleClear}>{t('actions.clear')}</Button>
  </div>
}
